import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { ReactComponent as AudioPlayIcon } from '@assets/images/Icons/Audio_player_play_2.svg'
import { ReactComponent as AudioPauseIcon } from '@assets/images/Icons/Audio_player_pause_2.svg'
import { ReactComponent as AudioBack10Icon } from '@assets/images/Icons/Audio_player_back_10.svg'
import { ReactComponent as AudioForward10Icon } from '@assets/images/Icons/Audio_player_forward_10.svg'
import { ReactComponent as AudioVolumeIcon } from '@assets/images/Icons/Audio_player_volume.svg'
import { ReactComponent as AudioVolumeMuteIcon } from '@assets/images/Icons/Audio_player_volume_mute.svg'
import type { AudioPlayerProps, AudioPlayerSpeed } from './AudioPlayer.types'
import { AUDIO_PLAYER_SPEEDS } from './AudioPlayer.types'
import {
  formatAudioPlayerTime,
  parseAudioPlayerSpeed,
} from './AudioPlayer.utils'
import './AudioPlayer.css'
import { useCallRecordingObjectUrl } from '@hooks/useCallRecording'
import { logger } from '@utils/safeLogger'

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  defaultVolume = 100,
  defaultSpeed = '1.0x',
  disabled = false,
  darkMode = false,
  className = '',
  hideSkip = false,
  hideVolume = false,
  hideSpeed = false,
  previewState,
  previewProgress = 35,
  previewCurrentTime = 42,
  previewDuration = 180,
  onPlay,
  onPause,
  onEnded,
}) => {
  const playbackSrc = useCallRecordingObjectUrl(src)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(defaultVolume)
  const [isMuted, setIsMuted] = useState(false)
  const [isShowVolume, setIsShowVolume] = useState(false)
  const [speedIndex, setSpeedIndex] = useState(() => {
    const index = AUDIO_PLAYER_SPEEDS.indexOf(defaultSpeed)
    return index >= 0 ? index : 1
  })

  const isPreview = previewState != null
  const isDisabled =
    disabled || previewState === 'disabled' || (!playbackSrc && !isPreview)
  const previewPlaying = previewState === 'playing'
  const showPlaying = isPreview ? previewPlaying : isPlaying
  const displayProgress = isPreview ? previewProgress : progress
  const displayCurrent = isPreview ? previewCurrentTime : currentTime
  const displayDuration = isPreview ? previewDuration : duration

  const isInterruptedPlayError = (error: unknown): boolean => {
    if (!(error instanceof DOMException)) {
      return false
    }
    return (
      error.name === 'AbortError' ||
      error.message.toLowerCase().includes('interrupted')
    )
  }

  const syncProgress = useCallback((): void => {
    const audio = audioRef.current
    if (!audio || !audio.duration) {
      return
    }
    setCurrentTime(audio.currentTime)
    setProgress((audio.currentTime / audio.duration) * 100)
  }, [])

  useEffect(() => {
    if (isPreview) {
      return undefined
    }

    const audio = audioRef.current
    if (!audio || !playbackSrc) {
      return undefined
    }

    setIsPlaying(false)
    setProgress(0)
    setCurrentTime(0)
    setDuration(0)
    // Nested <source> updates do not reload media; set src + load() for take changes.
    audio.src = playbackSrc
    audio.load()

    const handleLoadedMetadata = (): void => {
      if (audio.duration) {
        setDuration(audio.duration)
      }
    }

    const handleTimeUpdate = (): void => {
      syncProgress()
    }

    const handleEnded = (): void => {
      setIsPlaying(false)
      setCurrentTime(0)
      setProgress(0)
      onEnded?.()
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.pause()
    }
  }, [isPreview, onEnded, playbackSrc, syncProgress])

  useEffect(() => {
    if (isPreview || !audioRef.current) {
      return
    }
    audioRef.current.volume = volume / 100
  }, [isPreview, volume])

  useEffect(() => {
    if (isPreview || !audioRef.current) {
      return
    }
    audioRef.current.playbackRate = parseAudioPlayerSpeed(
      AUDIO_PLAYER_SPEEDS[speedIndex],
    )
  }, [isPreview, speedIndex])

  const handlePlayButton = (): void => {
    if (isDisabled || isPreview || !audioRef.current) {
      return
    }

    const nextPlaying = !isPlaying
    setIsPlaying(nextPlaying)
    if (nextPlaying) {
      audioRef.current
        .play()
        .then(() => {
          onPlay?.()
        })
        .catch((error: unknown) => {
          if (isInterruptedPlayError(error)) {
            return
          }
          setIsPlaying(false)
          logger.error('Error playing audio:', error)
        })
    } else {
      audioRef.current.pause()
      onPause?.()
    }
  }

  const handleRewind = (): void => {
    if (isDisabled || isPreview || !audioRef.current) {
      return
    }
    audioRef.current.currentTime = Math.max(
      0,
      audioRef.current.currentTime - 10,
    )
  }

  const handleForward = (): void => {
    if (isDisabled || isPreview || !audioRef.current) {
      return
    }
    audioRef.current.currentTime = Math.min(
      audioRef.current.duration || 0,
      audioRef.current.currentTime + 10,
    )
  }

  const handleProgressClick = (
    event: ReactMouseEvent<HTMLDivElement>,
  ): void => {
    if (
      isDisabled ||
      isPreview ||
      !audioRef.current ||
      !progressBarRef.current ||
      !audioRef.current.duration
    ) {
      return
    }

    const rect = progressBarRef.current.getBoundingClientRect()
    const ratio = Math.min(
      Math.max((event.clientX - rect.left) / rect.width, 0),
      1,
    )
    audioRef.current.currentTime = ratio * audioRef.current.duration
  }

  const handleVolumeButton = (): void => {
    if (isDisabled || isPreview || !audioRef.current) {
      return
    }
    const nextMuted = !isMuted
    setIsMuted(nextMuted)
    audioRef.current.muted = nextMuted
  }

  const handleVolumeChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (isDisabled || isPreview || !audioRef.current) {
      return
    }
    const nextVolume = Number(event.target.value)
    setVolume(nextVolume)
    audioRef.current.volume = nextVolume / 100
    if (nextVolume === 0) {
      setIsMuted(true)
      audioRef.current.muted = true
    } else if (isMuted) {
      setIsMuted(false)
      audioRef.current.muted = false
    }
  }

  const handleSpeedChange = (): void => {
    if (isDisabled || isPreview) {
      return
    }
    setSpeedIndex((index) => (index + 1) % AUDIO_PLAYER_SPEEDS.length)
  }

  const classNames = [
    'ds-audio-player',
    darkMode ? 'ds-audio-player--dark' : '',
    isDisabled ? 'ds-audio-player--disabled' : '',
    showPlaying ? 'ds-audio-player--playing' : '',
    isPreview ? 'ds-audio-player--preview' : '',
    previewState ? `ds-audio-player--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const speedLabel: AudioPlayerSpeed = AUDIO_PLAYER_SPEEDS[speedIndex]
  const PlayPauseIcon = showPlaying ? AudioPauseIcon : AudioPlayIcon
  const VolumeIcon = isMuted ? AudioVolumeMuteIcon : AudioVolumeIcon

  return (
    <div className={classNames} role="group" aria-label="Audio player">
      {!isPreview && playbackSrc ? (
        <audio
          ref={audioRef}
          controls={false}
          preload="metadata"
          src={playbackSrc}
        />
      ) : null}

      {!hideSkip ? (
        <button
          type="button"
          className="ds-audio-player__skip ds-audio-player__skip--back"
          aria-label="Rewind 10 seconds"
          disabled={isDisabled}
          onClick={handleRewind}
        >
          <AudioBack10Icon
            className="ds-audio-player__icon"
            aria-hidden="true"
          />
        </button>
      ) : null}

      <button
        type="button"
        className="ds-audio-player__play"
        aria-label={showPlaying ? 'Pause' : 'Play'}
        disabled={isDisabled}
        onClick={handlePlayButton}
      >
        <PlayPauseIcon className="ds-audio-player__icon" aria-hidden="true" />
      </button>

      {!hideSkip ? (
        <button
          type="button"
          className="ds-audio-player__skip ds-audio-player__skip--forward"
          aria-label="Forward 10 seconds"
          disabled={isDisabled}
          onClick={handleForward}
        >
          <AudioForward10Icon
            className="ds-audio-player__icon"
            aria-hidden="true"
          />
        </button>
      ) : null}

      <div className="ds-audio-player__timeline">
        <span className="ds-audio-player__time">
          {formatAudioPlayerTime(displayCurrent)}
        </span>
        <div
          className="ds-audio-player__progress"
          ref={progressBarRef}
          role="slider"
          tabIndex={isDisabled ? -1 : 0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(displayProgress)}
          aria-label="Seek"
          onClick={handleProgressClick}
        >
          <div className="ds-audio-player__track">
            <div
              className="ds-audio-player__fill"
              style={{
                width:
                  displayProgress > 0
                    ? `calc(${displayProgress}% - ${(displayProgress / 100) * 4}px)`
                    : '0px',
              }}
            >
              <span
                className="ds-audio-player__handle"
                style={{
                  left: displayProgress === 0 ? '-4px' : 'calc(100% - 7px)',
                }}
              />
            </div>
          </div>
        </div>
        <span className="ds-audio-player__time">
          {formatAudioPlayerTime(displayDuration)}
        </span>
      </div>

      {!hideVolume ? (
        <div
          className="ds-audio-player__volume"
          onMouseEnter={() => !isDisabled && setIsShowVolume(true)}
          onMouseLeave={() => setIsShowVolume(false)}
        >
          <button
            type="button"
            className="ds-audio-player__volume-btn"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            disabled={isDisabled}
            onClick={handleVolumeButton}
          >
            <VolumeIcon className="ds-audio-player__icon" aria-hidden="true" />
          </button>
          {isShowVolume && !isDisabled ? (
            <input
              className="ds-audio-player__volume-slider"
              type="range"
              min={0}
              max={100}
              step={1}
              value={volume}
              aria-label="Volume"
              onChange={handleVolumeChange}
            />
          ) : null}
        </div>
      ) : null}

      {!hideSpeed ? (
        <button
          type="button"
          className="ds-audio-player__speed"
          aria-label="Change playback speed"
          disabled={isDisabled}
          onClick={handleSpeedChange}
        >
          {speedLabel}
        </button>
      ) : null}
    </div>
  )
}

export default AudioPlayer
